import { useEffect, useRef, useState } from "react";
import { Alert, Button, Group, Modal } from "@mantine/core";
import { IconCamera, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

export function DocumentCameraModal({
  opened,
  onClose,
  onCapture,
}: {
  opened: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setError(null);

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.setAttribute("playsinline", "true");
          await video.play();
        }
      } catch (err) {
        setError(t("rma.boxes.docs.cameraError", { error: String(err).slice(0, 120) }));
      }
    }
    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    };
  }, [opened, t]);

  function capture() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const file = new File([blob], `document-${ts}.jpg`, { type: "image/jpeg" });
        onCapture(file);
        onClose();
      },
      "image/jpeg",
      0.9,
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("rma.boxes.docs.cameraTitle")} centered size="lg">
      {error ? (
        <Alert color="red">{error}</Alert>
      ) : (
        <>
          <video
            ref={videoRef}
            style={{ width: "100%", borderRadius: 8, background: "#000" }}
            muted
            playsInline
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" leftSection={<IconX size={16} />} onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button leftSection={<IconCamera size={16} />} onClick={capture}>
              {t("rma.boxes.docs.takePhoto")}
            </Button>
          </Group>
        </>
      )}
    </Modal>
  );
}
